import { useEffect, useRef } from 'react'

/**
 * Attaches an IntersectionObserver to the returned ref.
 * When the element enters the viewport it receives the "visible" class,
 * triggering the CSS reveal / scale-in / reveal-left / reveal-right /
 * reveal-blur transition defined in index.css.
 *
 * @param {object} options
 * @param {number} options.threshold  — 0–1, default 0.15
 * @param {string} options.rootMargin — default '0px 0px -60px 0px'
 */
export function useScrollReveal({
  threshold = 0.15,
  rootMargin = '0px 0px -60px 0px',
} = {}) {
  const ref = useRef(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('visible')
          observer.unobserve(el) // fire once
        }
      },
      { threshold, rootMargin },
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [threshold, rootMargin])

  return ref
}

/**
 * Observes multiple child elements inside a container.
 * Each child with class "reveal", "reveal-left", "reveal-right",
 * "reveal-blur", or "scale-in" gets "visible" added sequentially.
 */
export function useStaggerReveal({
  threshold = 0.1,
  rootMargin = '0px 0px -40px 0px',
} = {}) {
  const containerRef = useRef(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const SELECTOR = '.reveal, .reveal-left, .reveal-right, .reveal-blur, .scale-in'
    const children = Array.from(container.querySelectorAll(SELECTOR))

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible')
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold, rootMargin },
    )

    children.forEach((child) => observer.observe(child))
    return () => observer.disconnect()
  }, [threshold, rootMargin])

  return containerRef
}
